/**
 * Frontend permissions — mirrors api/src/auth/permissions.ts.
 *
 * Server is source of truth (it always re-checks). This module exists so the
 * UI can hide nav items + disable buttons before the user clicks them. Keep
 * the two files in sync — server-side denies will surface as 403s otherwise.
 */

export type Role = 'owner' | 'admin' | 'manager' | 'operator' | 'support' | 'viewer' | 'staff_viewer' | 'intake_viewer' | 'tenant_admin' | 'avery_agent'

export const ROLES: Role[] = ['owner', 'admin', 'manager', 'operator', 'support', 'viewer', 'staff_viewer', 'intake_viewer', 'tenant_admin', 'avery_agent']

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  operator: 'Operator',
  support: 'Support',
  viewer: 'Viewer',
  staff_viewer: 'Staff viewer',
  intake_viewer: 'Intake viewer',
  tenant_admin: 'Tenant',
  avery_agent: 'Avery agent',
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Full access. Manages other owners + Parkify-internal data.',
  admin: 'Parkify-internal staff. Manages users, sees internal usage, edits everything.',
  manager: 'Client lead. Edits zones/rates/permits/validations. Sees analytics + accounting.',
  operator: 'Day-to-day ops. Replies to support, issues refunds, edits validations + permits. Sees accounting + analytics.',
  support: 'Parkify-internal support. Inbox, transactions, refunds. No edits to zones/rates/permits.',
  viewer: 'Read-only access to home, transactions, accounting, analytics.',
  staff_viewer: 'Parkify staff, read-only across every client: home, transactions, accounting, customers, analytics. Cannot edit, refund, pay out or manage anyone.',
  intake_viewer: 'Parkify staff, read-only access to property intake submissions only.',
  tenant_admin: "Tenant self-serve portal user. Views and issues only their own tenant's validations (the operator sets the programs) + sees their redemption history. Never accesses the client console.",
  avery_agent: 'Machine principal for Avery autonomy (PR 18). Least-privilege: only edit.rates, per-tenant scoped, never money/admin. Never a login role — issued only by the server-side minter.',
}

export const ROLE_COLORS: Record<Role, string> = {
  owner: '#7F3F98',
  admin: '#213F99',
  manager: '#15803d',
  operator: '#F16521',
  support: '#0EA5E9',
  viewer: '#64748b',
  staff_viewer: '#475569',
  intake_viewer: '#6d28d9',
  tenant_admin: '#0d9488',
  avery_agent: '#9333ea',
}

export type Permission =
  | 'view.home' | 'view.support' | 'view.intake_submissions' | 'view.transactions' | 'view.zones'
  | 'view.qr'   | 'view.permits' | 'view.validations'  | 'view.accounting'
  | 'view.analytics' | 'view.integrations'
  | 'view.parkify_admin' | 'view.client_admin'
  | 'view.customers'
  | 'view.subscribers'
  | 'view.disputes'
  | 'view.avery'
  | 'edit.zones' | 'edit.zones.internal' | 'edit.rates' | 'edit.validations' | 'edit.permits' | 'edit.qr'
  | 'manage.subscribers'
  | 'manage.disputes'
  | 'refund.session' | 'extend.session' | 'payout.instant' | 'reply.support'
  // Mirrors api/src/auth/permissions.ts — send a coverage-unconfirmed goodwill
  // forward to an enforcement partner after confirming coverage by hand.
  | 'override.enforcement_coverage'
  // Mirrors api/src/auth/permissions.ts — send a manual enforcement forward
  // that states a citation/notice number OCR never captured, after confirming
  // it by hand against the customer's photo.
  | 'override.enforcement_citation'
  // Mirrors api/src/auth/permissions.ts — refund past the CUMULATIVE per-incident
  // cap. Holding refund.session lets you refund a session; it does not let you
  // decide one complaint is worth a fourth refund. owner/admin ONLY, pointedly
  // not 'support'.
  | 'override.refund_thread_total'
  // Mirrors api/src/auth/permissions.ts — read what Parkify owes a held-route
  // owner and record a payment made to them off platform. Owner only to start.
  | 'edit.finance.remittances'
  | 'manage.users' | 'manage.client_users' | 'manage.clients' | 'manage.tenants'
  | 'view.internal_usage' | 'manage.integrations' | 'view.audit'
  | 'manage.roles'
  | 'view.enforcement' | 'edit.enforcement' | 'issue.notice'
  // Mirrors api/src/auth/permissions.ts: use Parkify Patrol in the zones inside
  // your scope. No role by default; the ordinary grant is a per-person
  // `checkPlate` on a team member (Client Admin, Team). A role ticked here gives
  // it to every user of that role.
  | 'check.plate'
  | 'view.tenant_portal'

export type PermissionMatrix = Record<Permission, Role[]>

export const PERMISSION_LABELS: Record<Permission, string> = {
  'view.home':           'View Home',
  'view.support':        'View Support',
  'view.intake_submissions': 'View Intake Submissions (Parkify staff only)',
  'view.transactions':   'View Transactions',
  'view.zones':          'View Zones',
  'view.qr':             'View QR codes',
  'view.permits':        'View Permits',
  'view.validations':    'View Validations',
  'view.accounting':     'View Accounting',
  'view.analytics':      'View Analytics',
  'view.integrations':   'View Integrations',
  'view.parkify_admin':  'View Parkify Admin',
  'view.client_admin':   'View Client Admin',
  'view.customers':      'View Customers',
  'view.subscribers':    'View Subscribers',
  'view.disputes':       'View Disputes',
  'view.avery':          'View Avery',
  'edit.zones':          'Edit zones',
  'edit.zones.internal': 'Edit Parkify-internal zone fields',
  'edit.rates':          'Edit rates',
  'edit.validations':    'Edit validations',
  'edit.permits':        'Edit permits',
  'edit.qr':             'Upload/remove custom QR images (Parkify-internal only)',
  'refund.session':      'Refund sessions',
  'extend.session':      'Extend sessions',
  'payout.instant':      'Instant payouts',
  'reply.support':       'Reply to support',
  'override.enforcement_coverage': 'Override enforcement coverage check',
  'override.enforcement_citation': 'Override enforcement citation-number check',
  'override.refund_thread_total': 'Override per-ticket cumulative refund cap',
  'edit.finance.remittances': 'Record held-funds remittances',
  'manage.subscribers':  'Manage subscribers',
  'manage.disputes':     'Manage disputes',
  'manage.users':        'Manage Parkify staff',
  'manage.client_users': 'Manage client team',
  'manage.clients':      'Manage client orgs',
  'manage.tenants':      'Manage tenants',
  'manage.roles':        'Manage roles',
  'view.internal_usage': 'View internal usage',
  'manage.integrations': 'Manage integrations',
  'view.audit':          'View audit log',
  'view.enforcement':    'View Enforcement',
  'edit.enforcement':    'Edit notices',
  'issue.notice':        'Issue notices',
  'check.plate':         'Check plates in Patrol (every user of the role)',
  'view.tenant_portal':  'View tenant portal',
}

const PERMS: Record<Permission, Role[]> = {
  'view.home':           ['owner', 'admin', 'manager', 'operator', 'support', 'viewer', 'staff_viewer'],
  'view.support':        ['owner', 'admin', 'manager', 'operator', 'support'],
  'view.intake_submissions': ['owner', 'admin', 'support', 'intake_viewer'],
  'view.transactions':   ['owner', 'admin', 'manager', 'operator', 'support', 'viewer', 'staff_viewer'],
  'view.zones':          ['owner', 'admin', 'manager', 'operator', 'viewer'],
  'view.qr':             ['owner', 'admin', 'manager', 'operator'],
  'view.permits':        ['owner', 'admin', 'manager', 'operator', 'viewer'],
  'view.validations':    ['owner', 'admin', 'manager', 'operator', 'viewer', 'tenant_admin'],
  'view.accounting':     ['owner', 'admin', 'manager', 'operator', 'viewer', 'staff_viewer'],
  'view.analytics':      ['owner', 'admin', 'manager', 'operator', 'viewer', 'staff_viewer'],
  'view.integrations':   ['owner', 'admin'],
  'view.parkify_admin':  ['owner', 'admin'],
  'view.client_admin':   ['owner', 'admin', 'manager'],
  'view.customers':      ['owner', 'admin', 'manager', 'operator', 'support', 'viewer', 'staff_viewer'],
  'view.subscribers':    ['owner', 'admin', 'manager', 'operator', 'support', 'viewer', 'staff_viewer'],
  'view.disputes':       ['owner', 'admin', 'manager', 'operator', 'support', 'viewer', 'staff_viewer'],
  'view.avery':          ['owner', 'admin', 'manager', 'operator', 'viewer'],
  'edit.zones':          ['owner', 'admin', 'manager'],
  // Parkify-internal-only zone fields (billing/payout routing, enforcement
  // policy, integrations, active + public-visibility toggles) — a client
  // manager/operator can edit their own zone's basics via edit.zones but must
  // not see or touch these. Mirrors api/src/auth/permissions.ts (PR
  // zone-field-gate-api) — the server silently strips these fields from
  // update requests when the actor lacks this permission, so this is a UI
  // hide, not the enforcement boundary.
  'edit.zones.internal': ['owner', 'admin'],
  // avery_agent (machine principal, PR 18) — only grant it holds; gates the server
  // apply engine's ported config kinds. Mirrors api/src/auth/permissions.ts.
  'edit.rates':          ['owner', 'admin', 'manager', 'avery_agent'],
  'edit.validations':    ['owner', 'admin', 'manager', 'operator', 'tenant_admin'],
  'edit.permits':        ['owner', 'admin', 'manager', 'operator'],
  // Parkify-internal ONLY. Uploading/removing a custom QR image swaps the
  // printed artwork clients scan in the field — deliberately excluded from
  // edit.zones/edit.validations (which client staff hold) so no client-side
  // role can touch it, mirroring override.enforcement_coverage.
  'edit.qr':             ['owner', 'admin'],
  'refund.session':      ['owner', 'admin', 'manager', 'operator', 'support'],
  'extend.session':      ['owner', 'admin', 'manager', 'operator'],
  'payout.instant':      ['owner', 'admin', 'manager'],
  'reply.support':       ['owner', 'admin', 'manager', 'operator', 'support'],
  'override.enforcement_coverage': ['owner', 'admin'],
  'override.enforcement_citation': ['owner', 'admin'],
  'override.refund_thread_total': ['owner', 'admin'],
  'edit.finance.remittances': ['owner'],
  'manage.subscribers':  ['owner', 'admin', 'manager', 'operator', 'support'],
  'manage.disputes':     ['owner', 'admin', 'manager', 'operator', 'support'],
  'manage.users':        ['owner', 'admin'],
  'manage.client_users': ['owner', 'admin', 'manager'],
  'manage.clients':      ['owner', 'admin'],
  'manage.tenants':      ['owner', 'admin', 'manager', 'operator'],
  'manage.roles':        ['owner', 'admin'],
  'view.internal_usage': ['owner', 'admin'],
  'manage.integrations': ['owner', 'admin'],
  'view.audit':          ['owner', 'admin', 'manager'],
  'view.enforcement':    ['owner', 'admin', 'manager', 'operator', 'viewer'],
  'edit.enforcement':    ['owner', 'admin', 'manager', 'operator'],
  'issue.notice':        ['owner', 'admin', 'manager', 'operator'],
  'check.plate':         [],
  'view.tenant_portal':  ['tenant_admin'],
}

export const ALL_PERMISSIONS = Object.keys(PERMS) as Permission[]

export function hasPermission(role: Role | undefined, perm: Permission): boolean {
  if (!role) return false
  return PERMS[perm]?.includes(role) ?? false
}

export function getPermissionsForRole(role: Role): Permission[] {
  return ALL_PERMISSIONS.filter(p => hasPermission(role, p))
}

// Group permissions by category for the Roles matrix view
export const PERMISSION_GROUPS: Array<{ label: string; perms: Permission[] }> = [
  { label: 'View access', perms: [
    'view.home', 'view.support', 'view.intake_submissions', 'view.transactions', 'view.zones',
    'view.qr', 'view.permits', 'view.validations', 'view.accounting',
    'view.analytics', 'view.integrations',
    'view.customers', 'view.subscribers', 'view.disputes',
    'view.parkify_admin', 'view.client_admin', 'view.tenant_portal',
    'view.avery',
  ]},
  { label: 'Edit + actions', perms: [
    'edit.zones', 'edit.zones.internal', 'edit.rates', 'edit.validations', 'edit.permits', 'edit.qr',
    'manage.subscribers', 'manage.disputes',
    'refund.session', 'extend.session', 'payout.instant', 'reply.support',
    'override.enforcement_coverage', 'override.enforcement_citation', 'override.refund_thread_total',
    'edit.finance.remittances',
    'view.enforcement', 'edit.enforcement', 'issue.notice', 'check.plate',
  ]},
  { label: 'Admin', perms: [
    'manage.users', 'manage.client_users', 'manage.clients', 'manage.tenants',
    'manage.roles', 'view.internal_usage',
    'manage.integrations', 'view.audit',
  ]},
]
